import React, { useRef, useEffect, useLayoutEffect, useState, useCallback, type CSSProperties } from 'react';
import { CloseOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Dropdown, type MenuProps } from 'antd';
import { history, useLocation, useModel } from '@umijs/max';
import { getViewPath, normalizePathname } from '@/utils/routeUtil';
import './index.less';

const TAGS_BAR_HEIGHT = 36;
const HEADER_HEIGHT = 56;
const SCROLL_STEP = 200;
const THEME_COLOR = '#F3AE4A';

const TagsView: React.FC = () => {
  const location = useLocation();
  const activePath = getViewPath(normalizePathname(location.pathname), location.search);
  const { visitedViews, closeView, closeOtherViews, closeAllViews } = useModel('tagsViewModel');
  const scrollRef = useRef<HTMLDivElement>(null);
  const [fixedStyle, setFixedStyle] = useState<CSSProperties>({});
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const updateScrollState = useCallback(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  const scrollToActiveTag = useCallback((instant = false) => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }
    const behavior = instant ? ('auto' as const) : ('smooth' as const);
    const activeEl = container.querySelector('.tags-view-item--selected') as HTMLElement | null;

    if (!activeEl) {
      container.scrollTo({ left: container.scrollWidth - container.clientWidth, behavior });
      updateScrollState();
      return;
    }

    const padding = 4;
    const containerRect = container.getBoundingClientRect();
    const activeRect = activeEl.getBoundingClientRect();
    const lastItem = container.querySelector('.tags-view-item:last-child');
    const isLastActive = activeEl === lastItem;

    if (activeRect.right > containerRect.right - padding) {
      const targetLeft = isLastActive
        ? container.scrollWidth - container.clientWidth
        : container.scrollLeft + (activeRect.right - containerRect.right) + padding;
      container.scrollTo({ left: targetLeft, behavior });
    } else if (activeRect.left < containerRect.left + padding) {
      container.scrollTo({
        left: container.scrollLeft - (containerRect.left - activeRect.left) - padding,
        behavior,
      });
    }
    updateScrollState();
  }, [updateScrollState]);

  useEffect(() => {
    const updatePosition = () => {
      const content = document.querySelector('.ant-pro-layout-content') as HTMLElement | null;
      if (!content) {
        return;
      }
      const rect = content.getBoundingClientRect();
      setFixedStyle({
        left: rect.left,
        width: rect.width,
      });
    };

    updatePosition();

    window.addEventListener('resize', updatePosition);
    const sider = document.querySelector('.ant-pro-sider') as HTMLElement | null;
    const observer = new ResizeObserver(() => {
      updatePosition();
      updateScrollState();
    });
    if (sider) {
      observer.observe(sider);
    }
    observer.observe(document.body);

    return () => {
      window.removeEventListener('resize', updatePosition);
      observer.disconnect();
    };
  }, [updateScrollState]);

  useEffect(() => {
    updateScrollState();
  }, [visitedViews, updateScrollState, fixedStyle.width]);

  const prevViewsCountRef = useRef(visitedViews.length);

  useLayoutEffect(() => {
    const isNewTagAdded = visitedViews.length > prevViewsCountRef.current;
    const isActiveTagMissing = !visitedViews.some((view) => view.path === activePath);
    prevViewsCountRef.current = visitedViews.length;

    const shouldInstantScroll = isNewTagAdded || isActiveTagMissing;

    let frame2 = 0;
    const frame1 = requestAnimationFrame(() => {
      scrollToActiveTag(shouldInstantScroll);
      frame2 = requestAnimationFrame(() => scrollToActiveTag(shouldInstantScroll));
    });
    return () => {
      cancelAnimationFrame(frame1);
      cancelAnimationFrame(frame2);
    };
  }, [activePath, visitedViews.length, scrollToActiveTag]);

  const handleScroll = (direction: 'left' | 'right') => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }
    const delta = direction === 'left' ? -SCROLL_STEP : SCROLL_STEP;
    container.scrollBy({ left: delta, behavior: 'smooth' });
  };

  const handleClick = (pathname: string, search: string) => {
    const path = getViewPath(pathname, search);
    if (path !== activePath) {
      history.push(path);
    }
  };

  const handleClose = (event: React.MouseEvent, path: string) => {
    event.stopPropagation();
    closeView(path, activePath);
  };

  const contextMenuItems: MenuProps['items'] = [
    {
      key: 'closeOthers',
      label: '关闭其他',
      onClick: () => closeOtherViews(activePath),
    },
    {
      key: 'closeAll',
      label: '关闭全部',
      onClick: () => closeAllViews(),
    },
  ];

  return (
    <>
      <div className="tags-view-placeholder" style={{ height: TAGS_BAR_HEIGHT }} />
      <div
        className="tags-view-wrapper"
        style={{ ...fixedStyle, top: HEADER_HEIGHT, height: TAGS_BAR_HEIGHT }}
      >
        {showLeftArrow && (
          <button
            type="button"
            className="tags-view-arrow tags-view-arrow-left"
            onClick={() => handleScroll('left')}
            aria-label="向左滚动标签"
          >
            <LeftOutlined />
          </button>
        )}
        <Dropdown menu={{ items: contextMenuItems }} trigger={['contextMenu']}>
          <div className="tags-view-scroll" ref={scrollRef} onScroll={updateScrollState}>
            <div className="tags-view">
              {visitedViews.map((tag) => {
                const isActive = tag.path === activePath;
                return (
                  <div
                    key={tag.path}
                    className={`tags-view-item${isActive ? ' tags-view-item--selected' : ''}`}
                    style={
                      isActive
                        ? {
                            color: '#fff',
                            background: THEME_COLOR,
                            fontWeight: 500,
                            fontSize: 14,
                            boxShadow: '0 1px 3px rgba(243, 174, 74, 0.45)',
                          }
                        : undefined
                    }
                    onClick={() => handleClick(tag.pathname, tag.search)}
                  >
                    <span className="tags-view-title">{tag.title}</span>
                    {!tag.affix && (
                      <span className="tags-view-close" onClick={(event) => handleClose(event, tag.path)}>
                        <CloseOutlined />
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </Dropdown>
        {showRightArrow && (
          <button
            type="button"
            className="tags-view-arrow tags-view-arrow-right"
            onClick={() => handleScroll('right')}
            aria-label="向右滚动标签"
          >
            <RightOutlined />
          </button>
        )}
      </div>
    </>
  );
};

export default TagsView;
